import { useMemo, useRef } from "react";
import { useSize } from "ahooks";

/**
 * 底部操作栏自动计算碰撞换行、平滑避让计算hooks
 */
export function useBottomBarAutoWrap(barSize: { width?: number; height?: number } | undefined) {
    const metaRef = useRef<HTMLDivElement | null>(null);
    const actionsRef = useRef<HTMLDivElement | null>(null);

    const metaSize = useSize(metaRef);
    const actionsSize = useSize(actionsRef);

    const layout = useMemo(() => {
        let isWrapped = false;
        let shiftRight = 0;

        if (!barSize?.width || !metaSize?.width || !actionsSize?.width) {
            return { isWrapped, shiftRight };
        }

        // actions 区域容器的内容可用宽度 (减去了容器左右的 padding: 10px 和 66px)
        const contentWidth = barSize.width - 10 - 66;
        const centerLeft = 10 + contentWidth / 2;
        const actionsLeft = centerLeft - actionsSize.width / 2;

        // 锁定 meta 的文本宽度
        const safeMetaWidth = Math.max(metaSize.width, 485);
        // 左边界：meta 的最右侧 X 坐标 (预留 20px )
        const metaRight = 10 + safeMetaWidth + 20;

        if (actionsLeft < metaRight) {
            // 中心居中已经会碰到 meta 了，算出如果要避开 meta，需要往右挪多少
            const requiredShift = metaRight - actionsLeft;

            // 最大能接受的居中挪动像素值容差（如果居中位置往右偏移超过 150px 就会显得很偏右,会有点不居中的感觉）
            const MAX_SHIFT = 150;

            if (requiredShift <= MAX_SHIFT) {
                //  如果向右挪让步了 requiredShift，actions的右边缘会不会直接撞到界面的最右边缘（比如 extra 按钮区）
                const actionsRightAfterShift = actionsLeft + actionsSize.width + requiredShift;
                // 右极限边界保留 padding-right
                const rightBoundary = barSize.width - 66;

                if (actionsRightAfterShift <= rightBoundary) {
                    // 没有碰到有边界，允许不用换行，只需要向右挪一下
                    shiftRight = requiredShift;
                } else {
                    // 挪了会让最右侧溢出时必须换行。
                    isWrapped = true;
                }
            } else {
                // 挪动幅度太大，超过偏右幅度，直接换行回到新一行的绝对的视觉中心！
                isWrapped = true;
            }
        }

        return { isWrapped, shiftRight };
    }, [barSize?.width, metaSize?.width, actionsSize?.width]);

    return {
        metaRef,
        actionsRef,
        isWrapped: layout.isWrapped,
        shiftRight: layout.shiftRight,
    };
}
